import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { Pillars } from "../../../../types";
import TotalScoreBadge from "./TotalScoreBadge";
import { isScoreAvailable } from "../../../../formatting/domain-value-manipulation";

export type PillarScoreBarProps = {
  label: string;
  score: number;
};

function PillarScoreBar({ label, score }: PillarScoreBarProps) {
  const lightColour = `pillars.${label.toLowerCase()}.light`;
  const darkColour = `pillars.${label.toLowerCase()}.dark`;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        role="meter"
        aria-label={`${label} score`}
        aria-valuemin={0}
        aria-valuemax={10}
        aria-valuenow={score}
        sx={{
          position: "relative",
          flex: 1,
          height: "75%",
          overflow: "hidden",
          borderRadius: 3,
          backgroundColor: "common.white",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            width: `${score * 10}%`,
            borderRadius: 1.5,
            backgroundColor: lightColour,
          }}
        />

        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{ position: "relative", height: "100%", px: 1.25 }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              flexShrink: 0,
              borderRadius: "50%",
              backgroundColor: darkColour,
            }}
          />
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1,
              fontWeight: 900,
              fontSize: "1.18em",
              color: "primary.main",
            }}
          >
            {label}
          </Typography>
        </Stack>
      </Box>

      <Typography
        variant="body1"
        sx={{
          width: 36,
          flexShrink: 0,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
          fontSize: "1rem",
        }}
      >
        {score.toFixed(1)}
      </Typography>
    </Stack>
  );
}

export type PillarScoresProps = {
  pillars?: Pillars;
  totalScore?: number;
};

function PillarScores({ pillars, totalScore }: PillarScoresProps) {
  const carbonScore = pillars?.carbon?.scores?.total;
  const biodiversityScore = pillars?.biodiversity?.scores?.total;
  const peopleScore = pillars?.people?.scores?.total;

  return (
    <Tooltip
      followCursor
      title={`This component demonstrates how these scores will appear on the
          earthly.org website`}
    >
      <Box
        data-testid="pillar-scores"
        sx={{
          width: "100%",
          minWidth: { xs: "200px", lg: "750px" },
          maxWidth: { xs: "600px", lg: "750px" },
          aspectRatio: "22/7",
          borderRadius: 2,
          backgroundColor: "background.tabCard",
          px: { xs: 2.5, sm: 3.5 },
          py: { xs: 2.5, sm: 3.25 },
          border: "solid 3px",
          borderColor: "background.banner",
        }}
      >
        <Box
          sx={{
            height: "90%",
            display: "flex",
            flexDirection: "row",
            alignItems: 4,
            rowGap: 3,
          }}
        >
          <TotalScoreBadge
            shouldDisplay={isScoreAvailable(totalScore)}
            score={totalScore?.toFixed(1)}
          />

          <Stack spacing={0.8} sx={{ width: "75%", padding: "10px" }}>
            {isScoreAvailable(carbonScore) && (
              <PillarScoreBar label="Carbon" score={carbonScore} />
            )}
            {isScoreAvailable(biodiversityScore) && (
              <PillarScoreBar label="Biodiversity" score={biodiversityScore} />
            )}
            {isScoreAvailable(peopleScore) && (
              <PillarScoreBar label="People" score={peopleScore} />
            )}
          </Stack>
        </Box>
      </Box>
    </Tooltip>
  );
}

export default PillarScores;
